import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import useCurrentState from "@/routes/useCurrentState";
import { statePath } from "@/lib/stateRegistry";
import ProviderLogo from "../components/ProviderLogo";
import ProviderGrid from "../components/ProviderGrid";
import { fetchProviders } from "../lib/api";

export default function Providers() {
  const state = useCurrentState();
  const [providers, setProviders] = useState([]);
  useEffect(() => { fetchProviders({ state: state.code }).then(setProviders).catch(() => {}); }, [state.code]);

  return (
    <div data-testid="providers-page" data-state={state.slug}>
      <Seo title={`${state.name} energy providers`} path={statePath(state.slug, "providers")} noindex />
      <section className="bg-[#F7F9FC] border-b border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
          <span className="pill mb-5">Energy providers</span>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight font-extrabold leading-tight max-w-[20ch] text-[#0A2540]">
            Energy providers in <span className="bg-[#16A34A] text-white px-2 rounded-md">{state.name}</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-[55ch]">
            Providers whose plans we show in {state.name}. This is not every provider available at your address.
          </p>
        </div>
      </section>

      {providers.length > 0 && (
        <section className="border-b border-slate-100">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14">
            <ProviderGrid providers={providers} />
          </div>
        </section>
      )}

      <section>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {providers.map((p) => (
            <article key={p.id} className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`provider-card-${p.slug}`}>
              <div className="flex items-start gap-4 mb-4">
                <ProviderLogo provider={p} size={56} />
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-2xl font-bold leading-tight text-[#0A2540]">{p.name}</h2>
                </div>
              </div>

              <p className="text-sm text-slate-700 mb-4 leading-relaxed">{p.description}</p>

              <div className="flex flex-wrap gap-1 mb-5">
                {p.states.map((s) => (
                  <span key={s} className="text-xs font-mono px-2 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-100">{s}</span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500 gap-3">
                <a href={`tel:${p.phone.replace(/[^\d]/g, '')}`} className="flex items-center gap-1.5 hover:text-[#0A2540]">
                  <Phone className="w-3 h-3" /> {p.phone}
                </a>
                <Link to={`${statePath(state.slug, "plans")}?provider_id=${p.id}`} className="text-sm font-semibold text-[#0A2540] hover:text-[#16A34A] flex items-center gap-1" data-testid={`view-plans-${p.slug}`}>
                  View plans <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
